import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router';
import './styles/tokens.css';
import './styles/app.css';
import { api, setCsrfToken, type Session } from './lib/api.js';
import { NavShell } from './shell/NavShell.js';
import { Home } from './screens/Home.js';
import { Wealth } from './screens/Wealth.js';
import { Sips } from './screens/Sips.js';
import { Epf } from './screens/Epf.js';
import { Login } from './screens/Login.js';
import { Goals } from './screens/Goals.js';
import { Insights } from './screens/Insights.js';
import { Tax } from './screens/Tax.js';
import { Members } from './screens/Members.js';
import { Activity } from './screens/Activity.js';
import { Connections } from './screens/Connections.js';
import { HoldingDetail } from './screens/HoldingDetail.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

// Routes mirror the IA — the URL scheme IS the architecture (Law 3's machine
// breadcrumb). /money/* stays reserved.
const router = createBrowserRouter([
  {
    element: <NavShell />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/wealth', element: <Wealth /> },
      // Detail sits UNDER its list, so the URL carries the lineage that no
      // visible breadcrumb does (Navigation Law 3).
      { path: '/wealth/holding/:id', element: <HoldingDetail /> },
      // SIPs sit under Wealth in meaning but get their own URL: "what am I
      // committed to every month?" is a question people arrive with already
      // formed, not one they reach by drilling into a holding.
      { path: '/sips', element: <Sips /> },
      { path: '/epf', element: <Epf /> },
      { path: '/goals', element: <Goals /> },
      { path: '/insights', element: <Insights /> },
      { path: '/tax', element: <Tax /> },
      { path: '/family', element: <Members /> },
      { path: '/activity', element: <Activity /> },
      { path: '/mailboxes', element: <Connections /> },
    ],
  },
]);

function App() {
  const [state, setState] = useState<'checking' | 'anonymous' | 'ready'>('checking');

  useEffect(() => {
    api
      .get<Session & { csrfToken: string }>('/auth/session')
      .then((s) => {
        setCsrfToken(s.csrfToken);
        setState('ready');
      })
      .catch(() => setState('anonymous'));
  }, []);

  if (state === 'checking') return null; // instant-or-nothing beats a flash of login
  if (state === 'anonymous') return <Login onSignedIn={() => setState('ready')} />;
  return <RouterProvider router={router} />;
}

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);

/*
 * Register the service worker — the last step of installability.
 *
 * After load, so it never competes with the first paint, and failures are
 * swallowed on purpose: the app works identically without it, and an install
 * hint is not worth a console error on a browser that does not support one.
 */
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
  });
}
